import React, { useEffect, useState } from 'react';
import { WarningCircle, X, Check, ArrowRight, Info } from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import { fetchBranches, fetchMvNumbers, fetchMvDetails, fetchMvLineItems, submitReverseMvVoucher, saveData } from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult, inlineNoticeColor } from '../utils/noData';

function MvCancelForm({ connectionId }) {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const [branches, setBranches] = useState([]);
  const [mvList, setMvList] = useState([]);
  const [branchCode, setBranchCode] = useState('');
  const [mvNo, setMvNo] = useState('');
  const [mvDetail, setMvDetail] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingMvNo, setPendingMvNo] = useState('');
  const [pendingSubscriber, setPendingSubscriber] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);
  const isValid = selectedIdx >= 0 && reason.trim().length >= 10 && reason.length <= 200;

  const showToast = (severity, summary, detail, sticky = false) => {
    setToast({ severity, summary, detail, sticky });
    if (!sticky) setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    if (!connectionId) return;
    (async () => {
      try {
        setBranches(await fetchBranches(connectionId));
      } catch (e) {
        setLoadError('Could not load branches from the client API');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId]);

  const handleBranch = async (value) => {
    setBranchCode(value);
    setMvNo('');
    setMvList([]);
    setMvDetail(null);
    setRows([]);
    setSelectedIdx(-1);
    if (!value) return;
    try {
      const list = await fetchMvNumbers(connectionId, value);
      setMvList(list);
      if (isEmptyResult(list)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (e) {
      setLoadError('Could not load MV numbers for that branch');
    }
  };

  const handleMv = (value) => {
    setMvNo(value);
    setMvDetail(null);
    setRows([]);
    setSelectedIdx(-1);
  };

  const handleShow = async () => {
    if (!mvNo) return;
    setLoading(true);
    setLoadError('');
    try {
      const detailsRows = await fetchMvDetails(connectionId, branchCode, mvNo);
      if (!detailsRows.length) {
        setLoadError('No details found for this MV number.');
        return;
      }
      const d = detailsRows[0];
      setMvDetail(d);
      const lineItems = await fetchMvLineItems(connectionId, branchCode, mvNo, d.tbl_trans_payment_voucher_id);
      setRows(lineItems || []);
      setSelectedIdx(-1);
      if (isEmptyResult(lineItems)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (e) {
      setLoadError('Could not load MV voucher details.');
    } finally {
      setLoading(false);
    }
  };

  /** Step 1: validate, stage the pending reversal, open the confirm modal. */
  const confirmSave = () => {
    setTouched(true);
    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please select a line item and enter a valid reason.');
      return;
    }

    const row = rows[selectedIdx];
    setPendingMvNo(mvNo);
    setPendingSubscriber(row.contact_name);
    setPendingReason(reason);
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  /** Step 2: fires only after the user clicks "Yes, Cancel Voucher" in the modal. */
  const doSave = async () => {
    const row = rows[selectedIdx];
    setSaving(true);
    try {
      await submitReverseMvVoucher(connectionId, {
        branchCode,
        paymentNumber: mvNo,
        paymentDate: mvDetail.payment_date,
        paymentVoucherId: mvDetail.tbl_trans_payment_voucher_id,
        contactId: row.contact_id,
        paymentVoucherDetailsId: row.tbl_trans_payment_voucher_details_id,
        reason,
      });
      try {
        await saveData([{
          schemaName: branchCode || null,
          loginName,
          loginTime: new Date().toISOString(),
          changeType: 'MV_CANCEL',
          oldData: {
            branchCode,
            paymentNumber: mvNo,
            paymentDate: mvDetail.payment_date,
            paymentVoucherId: mvDetail.tbl_trans_payment_voucher_id,
            contactId: row.contact_id,
            contactName: row.contact_name,
            paymentVoucherDetailsId: row.tbl_trans_payment_voucher_details_id,
            status: 'ACTIVE',
          },
          newData: { paymentNumber: mvNo, status: 'REVERSED' },
          reason: reason.trim(),
        }]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', `Voucher ${pendingMvNo} reversed.`, true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Voucher reversed, but audit log failed to save.', true);
        handleClear();
      }
    } catch (e) {
      setSaving(false);
      setShowConfirmModal(false);
      showToast('error', 'Cancel Failed', e?.response?.data?.detail || 'Could not cancel this voucher. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setBranchCode('');
    setMvNo('');
    setMvList([]);
    setMvDetail(null);
    setRows([]);
    setSelectedIdx(-1);
    setLoadError('');
    setReason('');
    setTouched(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="mv-cancel-toast" />

      <div key={formKey} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="mv-cancel-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
        <div style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>MV Cancel</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Voucher</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch and MV number, then fetch its details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Branch Name *</label>
            <SearchableSelect
              options={branches}
              value={branchCode}
              onChange={handleBranch}
              getLabel={(b) => b.branch_name}
              getValue={(b) => b.branch_code}
              placeholder="Search branch..."
              testId="mv-cancel-branch"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>MV No. *</label>
            <SearchableSelect
              options={mvList}
              value={mvNo}
              onChange={handleMv}
              getLabel={(m) => m.payment_number}
              getValue={(m) => String(m.payment_number)}
              placeholder={'Search MV No...'}
              disabled={!branchCode}
              testId="mv-cancel-mvno"
            />
          </div>
          <div>
            <button type="button" className="btn-primary" disabled={!mvNo} onClick={handleShow} data-testid="mv-cancel-show-button">Show</button>
          </div>
        </div>
        {loading && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '8px' }}>Loading voucher details...</div>}
        {loadError && <div style={{ fontSize: '11px', color: inlineNoticeColor(loadError), marginTop: '8px' }}>{loadError}</div>}

        {mvDetail && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Voucher Summary</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Read-only summary loaded from the selected MV</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Payment Date</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={mvDetail.payment_date || ''} readOnly data-testid="mv-cancel-payment-date" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Total Amount (₹)</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={mvDetail.total_paid_amount ?? ''} readOnly data-testid="mv-cancel-amount" />
              </div>
            </div>
          </>
        )}

        {mvDetail && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Voucher Line to Reverse</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>EasyChit reverses one line item at a time — select the one to cancel</div>
              </div>
            </div>

            {rows.length === 0 ? (
              <div style={{ padding: '12px', fontSize: '13px', color: '#9CA3AF', border: '1px dashed #E5E7EB', borderRadius: '6px' }}>
                No voucher details found for this MV.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }} data-testid="mv-cancel-table">
                <thead>
                  <tr style={{ borderBottom: '1px solid #E5E7EB', textAlign: 'left', color: '#6B7280', fontSize: '11px' }}>
                    <th style={{ padding: '8px', width: '32px' }}></th>
                    <th style={{ padding: '8px' }}>Subscriber Name</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #F3F4F6', background: selectedIdx === i ? '#EFF6FF' : undefined }}>
                      <td style={{ padding: '8px' }}>
                        <input type="radio" name="mv-cancel-row" checked={selectedIdx === i} onChange={() => setSelectedIdx(i)} data-testid={`mv-cancel-row-${i}`} />
                      </td>
                      <td style={{ padding: '8px' }}>{r.contact_name}</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: '#DC2626', fontWeight: '600' }}>{Number(r.ledger_Amount).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!mvDetail || selectedIdx < 0} hint="Select a voucher line to reverse in section C first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>D</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Cancellation</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={3}
          maxLength={200}
          placeholder="Enter reason (min 10 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="mv-cancel-reason"
        />
        <div style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : '#9CA3AF', marginTop: '2px', textAlign: 'right' }}>
          {reason.length} / 200
        </div>
        {reasonInvalid && <div style={{ fontSize: '11px', color: '#DC2626' }}>Minimum 10 characters required</div>}
        </SectionLock>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="mv-cancel-clear-button">Clear</button>
          <button type="button" onClick={confirmSave} className="btn-primary" disabled={selectedIdx < 0 || saving} data-testid="mv-cancel-save-button">
            {saving ? 'Cancelling...' : 'Cancel Voucher'}
          </button>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="mv-cancel-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <WarningCircle size={18} color="#DC2626" weight="fill" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Voucher Cancellation</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to reverse this voucher line item. This action cannot be undone.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>MV No.</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingMvNo}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Subscriber</p>
                  <p style={{ fontSize: '13px', color: '#DC2626', fontWeight: '600', margin: 0 }}>{pendingSubscriber}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="mv-cancel-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={doSave} disabled={saving} className="btn-primary" data-testid="mv-cancel-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Cancel Voucher
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MvCancelForm;